import { Stack, Paper, Group, Text, SimpleGrid, Anchor } from '@mantine/core';
import { Patient, Practitioner, ServiceRequest } from '@medplum/fhirtypes';
import { IconNotes } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useDateDisplay } from '@hooks/useDateDisplay';
import { OrderFormData } from './OrderForm';
import {
  getAnswerDeliveryDisplayName,
  getPatientOrPractitionerName,
} from '../utils/common-helpers';
import { LabeledField } from '@/components/LabeledField';
import { getCodeableConceptDisplay } from '../utils/specimen-utils';
import { DocumentReferenceDisplay } from '@/components/DocumentReferenceDisplay';
import { useOrderSupportingDocuments } from '../hooks/useOrderSupportingDocuments';

const LabeledItem = ({ label, value }: { label: string; value: string }) => {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed" tt="uppercase">
        {label}
      </Text>
      <Text>{value}</Text>
    </Stack>
  );
};

export interface OrderDisplayCardProps {
  patient: Patient | undefined;
  requester: Practitioner | undefined;
  formData: OrderFormData;
  serviceRequest?: ServiceRequest | null;
}

export function OrderDisplayCard({
  patient,
  requester,
  formData,
  serviceRequest,
}: OrderDisplayCardProps) {
  const { t } = useTranslation('lims');
  const formatDateTime = useDateDisplay();

  const { orderDocuments, authDocuments } = useOrderSupportingDocuments(
    serviceRequest ?? null
  );

  const hasClinicalNotes =
    formData.referralReason ||
    formData.symptomOnsetAge ||
    formData.firstSymptom ||
    formData.clinicalNotes;

  return (
    <Stack gap="md">
      {/* Top Section: Order Details in Two Columns */}
      <SimpleGrid cols={2} spacing="xs">
        <Stack gap={0}>
          <Text size="xs" c="dimmed" tt="uppercase">
            {t('orderDisplay.labels.patient')}
          </Text>
          {patient?.id ? (
            <Anchor component={Link} to={`/Patient/${patient.id}`}>
              {getPatientOrPractitionerName(patient)}
            </Anchor>
          ) : (
            <Text>{getPatientOrPractitionerName(patient)}</Text>
          )}
        </Stack>
        <Stack gap={0}>
          <Text size="xs" c="dimmed" tt="uppercase">
            {t('orderDisplay.labels.requester')}
          </Text>
          {requester?.id ? (
            <Anchor component={Link} to={`/Practitioner/${requester.id}`}>
              {getPatientOrPractitionerName(requester)}
            </Anchor>
          ) : (
            <Text>{getPatientOrPractitionerName(requester)}</Text>
          )}
        </Stack>

        <LabeledItem
          label={t('orderDisplay.labels.geneticTest')}
          value={formData.testDisplay || formData.testCode}
        />
        <LabeledItem
          label={t('orderDisplay.labels.sampleType')}
          value={getCodeableConceptDisplay(formData.sampleType)}
        />

        <LabeledItem
          label={t('orderDisplay.labels.answerDelivery')}
          value={getAnswerDeliveryDisplayName(formData.answerDelivery)}
        />
        <LabeledItem
          label={t('orderDisplay.labels.creationDate')}
          value={formatDateTime(serviceRequest?.authoredOn)}
        />
      </SimpleGrid>

      {authDocuments.length > 0 && (
        <Stack gap={4}>
          <Text size="xs" c="dimmed" tt="uppercase">
            {t('orderDisplay.labels.authorizationDocuments')}
          </Text>
          <DocumentReferenceDisplay file={authDocuments[0]} readonly />
        </Stack>
      )}

      {orderDocuments.length > 0 && (
        <Stack gap={4}>
          <Text size="xs" c="dimmed" tt="uppercase">
            {t('orderDisplay.labels.orderDocuments')}
          </Text>
          <DocumentReferenceDisplay file={orderDocuments[0]} readonly />
        </Stack>
      )}

      {/* Bottom Section: Clinical Notes */}
      {hasClinicalNotes && (
        <Paper p="md" radius="md" bg="yellow.0" withBorder>
          <Stack gap="xs">
            <Group gap="xs" align="center">
              <IconNotes size={18} />
              <Text fw={500}>{t('orderDisplay.labels.clinicalNotes')}</Text>
            </Group>

            <Stack gap={4}>
              {formData.referralReason && (
                <LabeledField
                  label={t('orderDisplay.labels.referralReason')}
                  value={formData.referralReason}
                />
              )}

              {formData.symptomOnsetAge && (
                <LabeledField
                  label={t('orderDisplay.labels.symptomOnsetAge')}
                  value={formData.symptomOnsetAge}
                />
              )}

              {formData.firstSymptom && (
                <LabeledField
                  label={t('orderDisplay.labels.firstSymptom')}
                  value={formData.firstSymptom}
                />
              )}

              {formData.clinicalNotes && (
                <LabeledField
                  label={t('orderDisplay.labels.clinicalNotes')}
                  value={formData.clinicalNotes}
                />
              )}
            </Stack>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
